import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PublicBookingLayout from '../components/layout/PublicBookingLayout';
import { useBooking } from '@/hooks/useBookingContext';
import { generateTimeSlots, getDisabledDates, TimeSlot } from '@/utils/timeSlotUtils';
import { toast } from 'sonner';
import DateTimeSelector from '@/components/booking/DateTimeSelector';
import BookingForm, { BookingFormValues } from '@/components/booking/BookingForm';
import BookingSummary from '@/components/booking/BookingSummary';
import ProviderInfoPanel from '@/components/booking/ProviderInfoPanel';
import ServiceDetailsPanel from '@/components/booking/ServiceDetailsPanel';
import MeetingTypeSelector from '@/components/booking/MeetingTypeSelector';
import PaymentSummary from '@/components/booking/PaymentSummary';
import { getUserTimezone } from '@/utils/timeZoneUtils';
import { supabase } from '@/integrations/supabase/client';
import { Separator } from '@/components/ui/separator';

const BookingPage: React.FC = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { currentBookingLink, setCurrentBookingLink } = useBooking();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [timezone, setTimezone] = useState<string>(getUserTimezone());
  const [selectedMeetingType, setSelectedMeetingType] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch booking link data when component mounts
  useEffect(() => {
    const fetchBookingLink = async () => {
      if (!username) return;

      try {
        console.log('Fetching booking link for username:', username);
        
        // Get the booking link directly using the username
        const { data: bookingLinks, error } = await supabase
          .from('booking_links')
          .select('*')
          .eq('username', username)
          .single();

        if (error) {
          console.error('Error fetching booking link:', error);
          toast.error('Unable to load booking information');
          return;
        }

        if (!bookingLinks) {
          console.error('No booking link found for username:', username);
          toast.error('No booking link found for this user');
          return;
        }

        console.log('Found booking link:', bookingLinks);
        setCurrentBookingLink(bookingLinks);
      } catch (error) {
        console.error('Error in fetchBookingLink:', error);
        toast.error('Failed to load booking information');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookingLink();
  }, [username, setCurrentBookingLink]);

  // Generate time slots when date changes
  useEffect(() => {
    if (selectedDate && currentBookingLink) {
      const slots = generateTimeSlots(
        selectedDate,
        currentBookingLink.duration,
        currentBookingLink.availability
      );
      setTimeSlots(slots);
      setSelectedTimeSlot(null);
    }
  }, [selectedDate, currentBookingLink]);

  // Reset time slot selection when timezone changes
  useEffect(() => {
    if (selectedDate && currentBookingLink) {
      const slots = generateTimeSlots(
        selectedDate,
        currentBookingLink.duration,
        currentBookingLink.availability
      );
      setTimeSlots(slots);
      setSelectedTimeSlot(null);
    }
  }, [timezone, selectedDate, currentBookingLink]);

  // Set initial meeting type
  useEffect(() => {
    if (currentBookingLink?.meetingTypes) {
      const enabledTypes = Object.entries(currentBookingLink.meetingTypes)
        .filter(([_, enabled]) => enabled)
        .map(([type]) => type);
      
      if (enabledTypes.length > 0 && !selectedMeetingType) {
        setSelectedMeetingType(enabledTypes[0]);
      }
    }
  }, [currentBookingLink, selectedMeetingType]);

  const sendConfirmationEmail = async (bookingData: any) => {
    try {
      const { data, error } = await supabase.functions.invoke('send-booking-confirmation', {
        body: { bookingData }
      });
      
      if (error) {
        console.error('Error sending confirmation email:', error);
        // Don't block the booking flow if email fails
        toast.error("Booking confirmed, but there was an issue sending the confirmation email.");
      } else {
        console.log('Confirmation email sent:', data);
      }
    } catch (error) {
      console.error('Error invoking email function:', error);
    }
  };

  const handleSubmit = async (data: BookingFormValues) => {
    if (!selectedDate || !selectedTimeSlot) {
      toast.error("Please select a date and time for your appointment");
      return;
    }

    if (!currentBookingLink?.id) {
      console.error('No booking link ID found:', currentBookingLink);
      toast.error("Unable to find booking link information. Please try again.");
      return;
    }

    if (!username) {
      console.error('No host username found');
      toast.error("Unable to identify the host. Please try again.");
      return;
    }

    setIsSubmitting(true);

    try {
      // First get the user_id for the host
      const { data: userData, error: userError } = await supabase
        .from('booking_links')
        .select('user_id')
        .eq('id', currentBookingLink.id)
        .single();

      if (userError || !userData?.user_id) {
        console.error('Error getting host user_id:', userError);
        toast.error("Unable to identify the host. Please try again.");
        return;
      }

      const bookingData = {
        user_id: userData.user_id,
        booking_link_id: currentBookingLink.id,
        name: data.name,
        email: data.email,
        phone: data.phone || null,
        date: selectedDate.toISOString().split('T')[0],
        start_time: selectedTimeSlot.startTime.toTimeString().split(' ')[0],
        end_time: selectedTimeSlot.endTime.toTimeString().split(' ')[0],
        timezone: timezone,
        meeting_type: selectedMeetingType,
        notes: data.notes || '',
        status: 'confirmed',
        payment_amount: currentBookingLink?.payment?.required 
          ? currentBookingLink.payment.amount 
          : 0
      };

      console.log('Attempting to create booking with data:', bookingData);

      const { data: booking, error } = await supabase
        .from('bookings')
        .insert(bookingData)
        .select();

      if (error) {
        console.error('Error creating booking:', error);
        console.error('Error details:', error.details, error.hint, error.message);
        toast.error("There was a problem scheduling your appointment. Please try again.");
        return;
      }

      const bookingWithId = { ...bookingData, id: booking?.[0]?.id };
      
      await sendConfirmationEmail(bookingWithId);
      toast.success("Appointment scheduled successfully!");
      
      navigate('/booking-confirmation', { 
        state: { bookingData: bookingWithId } 
      });
      
    } catch (error) {
      console.error('Error in booking submission:', error);
      toast.error("There was a problem scheduling your appointment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <PublicBookingLayout>
        <div className="container max-w-5xl mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading booking information...</p>
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  if (!currentBookingLink) {
    return (
      <PublicBookingLayout>
        <div className="container max-w-5xl mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-2">Booking Not Found</h1>
            <p className="text-muted-foreground">
              The booking link you're trying to access doesn't exist or has been removed.
            </p>
          </div>
        </div>
      </PublicBookingLayout>
    );
  }

  return (
    <PublicBookingLayout>
      <div className="container max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left column - Provider info and booking details */}
          <div className="lg:col-span-1 space-y-6">
            <ProviderInfoPanel 
              providerInfo={currentBookingLink?.providerInfo}
              providerName={username || 'Provider'}
            />
            
            <ServiceDetailsPanel 
              serviceDetails={currentBookingLink?.serviceDetails}
              duration={currentBookingLink?.duration || 30}
            />
            
            {selectedDate && selectedTimeSlot && (
              <BookingSummary 
                selectedDate={selectedDate}
                selectedTimeSlot={selectedTimeSlot}
                timezone={timezone}
              />
            )}

            {currentBookingLink?.payment?.required && currentBookingLink.payment.amount > 0 && (
              <PaymentSummary 
                serviceDetails={currentBookingLink.serviceDetails}
                paymentRequired={currentBookingLink.payment.required}
                amount={currentBookingLink.payment.amount}
              />
            )}
          </div>
          
          {/* Right column - Booking form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">Book with {username}</h1>
              <p className="text-muted-foreground">
                Select a date and time to schedule your appointment
              </p>
              {currentBookingLink && (
                <p className="text-sm text-muted-foreground mt-2">
                  {currentBookingLink.name} ({currentBookingLink.duration} min)
                </p>
              )}
            </div>
            
            <DateTimeSelector 
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              timeSlots={timeSlots}
              selectedTimeSlot={selectedTimeSlot}
              setSelectedTimeSlot={setSelectedTimeSlot}
              disabledDates={currentBookingLink ? getDisabledDates(currentBookingLink.availability) : undefined}
              timezone={timezone}
              setTimezone={setTimezone}
            />

            {selectedDate && selectedTimeSlot && currentBookingLink?.meetingTypes && (
              <MeetingTypeSelector 
                meetingTypes={currentBookingLink.meetingTypes}
                selectedType={selectedMeetingType}
                onSelect={setSelectedMeetingType}
                locations={currentBookingLink.locations}
              />
            )}

            {selectedDate && selectedTimeSlot && (
              <BookingForm 
                selectedDate={selectedDate}
                selectedTimeSlot={selectedTimeSlot}
                meetingTypes={currentBookingLink?.meetingTypes || {}}
                selectedMeetingType={selectedMeetingType}
                onMeetingTypeChange={setSelectedMeetingType}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>
      </div>
    </PublicBookingLayout>
  );
};

export default BookingPage;
